import type { AdminTenantOption } from '../components/AdminTenantSelector';
import type {
  CollaborationContact,
  CollaborationContactFormState,
  CollaborationInboundCustomerRelationship,
  CollaborationInboundCustomerRelationshipStatus,
  CollaborationInstance,
  CollaborationInboxItem,
  CollaborationPartner,
  CollaborationPartnerProfile,
  SupplierImportBatchSnapshot,
  CollaborationPolicy,
  CollaborationPolicyFormState,
  CollaborationPartnerTemplateReplyTo,
  CollaborationPartnerTemplateReplyToFormState,
  CollaborationPartnerTemplateAuthorisation,
  CollaborationPartnerTemplateAuthorisationFormState,
  CollaborationPrincipalContactChangeRequest,
  CollaborationShareInvitation
} from './collaborationControlPlaneShared';
import type {
  CollaborationActionEditableFieldKey,
  CollaborationFindingEditableFieldKey,
  CollaborationThreadSnapshot,
  CollaborationThreadSyncPackage,
  CollaborationThreadMessageTranslation
} from './collaborationThreadShared';

type CollaborationEditableFieldKey = CollaborationFindingEditableFieldKey | CollaborationActionEditableFieldKey;

interface JsonMessage {
  errorCode?: string;
  message?: unknown;
  detail?: string;
  error?: string;
  messageText?: string;
}

export class CollaborationRouteError extends Error {
  public errorCode?: string;

  constructor(message: string, errorCode?: string) {
    super(message);
    this.errorCode = errorCode;
  }
}

const readJson = async <T>(response: Response): Promise<T> => (await response.json().catch(() => ({}))) as T;

const failureMessage = (response: Response, payload: JsonMessage, fallback: string): string =>
  payload.messageText ??
  (typeof payload.message === 'string' ? payload.message : undefined) ??
  payload.detail ??
  payload.error ??
  `${fallback} (${response.status})`;

/**
 * Tracker #121: the collaboration page's own tenant list, open to every user
 * who may use collaboration (the SPFx deployment list needs tenant-scope
 * rights). Names only; the default is the sign-in tenant.
 */
export const loadCollaborationTenants = async (): Promise<{
  tenants: AdminTenantOption[];
  defaultTenantId: string;
}> => {
  const response = await fetch('/api/collaboration/tenants');
  if (!response.ok) {
    throw new Error(`Tenant list failed (${response.status})`);
  }
  const payload = await readJson<{ tenants?: AdminTenantOption[]; defaultTenantId?: string }>(response);
  const tenants = Array.isArray(payload.tenants) ? payload.tenants : [];
  return {
    tenants,
    defaultTenantId: payload.defaultTenantId || tenants[0]?.tenantId || ''
  };
};
/** Tracker #121: an installation whose SharePoint app the Start button opens. */
export interface CollaborationStartTarget {
  instanceId: string;
  displayName: string;
  appUrl: string;
}

/** Tracker #121 (owner option B): the app pages "Start collaboration" can open. */
export const loadCollaborationStartTargets = async (tenantId: string): Promise<CollaborationStartTarget[]> => {
  const params = new URLSearchParams({ tenantId });
  const response = await fetch(`/api/collaboration/start-targets?${params.toString()}`);
  const payload = await readJson<{ installations?: CollaborationStartTarget[] } & JsonMessage>(response);
  if (!response.ok) {
    throw new Error(failureMessage(response, payload, 'Installations load failed'));
  }
  return Array.isArray(payload.installations)
    ? payload.installations.filter((entry) => typeof entry?.appUrl === 'string' && entry.appUrl.startsWith('https://'))
    : [];
};

export const loadCollaborationPolicyForTenant = async (tenantId: string): Promise<CollaborationPolicy | null> => {
  const params = new URLSearchParams({ tenantId });
  const response = await fetch(`/api/collaboration/policy?${params.toString()}`);
  const payload = await readJson<{ policy?: CollaborationPolicy } & JsonMessage>(response);
  if (!response.ok) {
    throw new Error(failureMessage(response, payload, 'Supplier collaboration policy load failed'));
  }
  return payload.policy ?? null;
};

/** Tracker #117: the starter's collaboration tier and, for Free, its monthly allowance. */
export interface CollaborationShareAllowance {
  tier: 'full' | 'text_only' | 'none';
  allowance?: {
    usedThisPeriod: number;
    included: number;
    remaining: number;
    allowShareOverage: boolean;
    meteringEnabled: boolean;
    periodStart: string;
    periodEnd: string;
  };
}

export const loadCollaborationShareAllowance = async (tenantId: string): Promise<CollaborationShareAllowance> => {
  const params = new URLSearchParams({ tenantId });
  const response = await fetch(`/api/collaboration/share-allowance?${params.toString()}`);
  const payload = await readJson<Partial<CollaborationShareAllowance> & JsonMessage>(response);
  if (!response.ok) {
    throw new Error(failureMessage(response, payload, 'Collaboration allowance load failed'));
  }
  const tier = payload.tier === 'full' || payload.tier === 'text_only' ? payload.tier : 'none';
  return { tier, allowance: payload.allowance };
};

export const saveCollaborationShareOverage = async ({
  tenantId,
  allowShareOverage
}: {
  tenantId: string;
  allowShareOverage: boolean;
}): Promise<void> => {
  const response = await fetch('/api/collaboration/policy/share-overage', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId, allowShareOverage })
  });
  const payload = await readJson<JsonMessage>(response);
  if (!response.ok) {
    throw new Error(failureMessage(response, payload, 'Collaboration overage setting save failed'));
  }
};

export const loadCollaborationInstancesForTenant = async (tenantId: string): Promise<CollaborationInstance[]> => {
  const params = new URLSearchParams({ tenantId });
  const response = await fetch(`/api/collaboration/instances?${params.toString()}`);
  const payload = await readJson<{ instances?: CollaborationInstance[] } & JsonMessage>(response);
  if (!response.ok) {
    throw new Error(failureMessage(response, payload, 'Collaboration installations load failed'));
  }
  return Array.isArray(payload.instances) ? payload.instances : [];
};

export const loadCollaborationKnownTemplateKeys = async (tenantId: string): Promise<string[]> => {
  const params = new URLSearchParams({ tenantId });
  const response = await fetch(`/api/collaboration/template-keys?${params.toString()}`);
  const payload = await readJson<{ templateKeys?: string[] } & JsonMessage>(response);
  if (!response.ok) {
    throw new Error(failureMessage(response, payload, 'Template suggestions load failed'));
  }
  return Array.isArray(payload.templateKeys)
    ? payload.templateKeys.filter((value): value is string => typeof value === 'string')
    : [];
};

export const loadCollaborationPrincipalContactChanges = async ({
  tenantId,
  partnerId
}: {
  tenantId: string;
  partnerId: string;
}): Promise<CollaborationPrincipalContactChangeRequest[]> => {
  const params = new URLSearchParams({ tenantId, partnerId });
  const response = await fetch(`/api/collaboration/principal-contact-change-requests?${params.toString()}`);
  const payload = await readJson<{ requests?: CollaborationPrincipalContactChangeRequest[] } & JsonMessage>(response);
  if (!response.ok) {
    throw new Error(failureMessage(response, payload, 'Principal-contact requests load failed'));
  }
  return Array.isArray(payload.requests) ? payload.requests : [];
};

export const loadCollaborationData = async ({
  tenantId,
  canManage,
  canInitiateShare
}: {
  tenantId: string;
  canManage: boolean;
  canInitiateShare: boolean;
}): Promise<{
  policy: CollaborationPolicy | null;
  inbox: CollaborationInboxItem[];
  shares: CollaborationShareInvitation[];
  partners: CollaborationPartner[];
  contacts: CollaborationContact[];
  instances: CollaborationInstance[];
  principalChangeRequests: CollaborationPrincipalContactChangeRequest[];
  partnerTemplateReplyTos: CollaborationPartnerTemplateReplyTo[];
  partnerTemplateAuthorisations: CollaborationPartnerTemplateAuthorisation[];
  knownTemplateKeys: string[];
}> => {
  const params = new URLSearchParams({ tenantId });
  const [policyRes, inboxRes, sharesRes] = await Promise.all([
    fetch(`/api/collaboration/policy?${params.toString()}`),
    fetch(`/api/collaboration/inbox?${params.toString()}`),
    fetch(`/api/collaboration/shares?${params.toString()}`)
  ]);

  const [policyPayload, inboxPayload, sharesPayload] = await Promise.all([
    readJson<{ policy?: CollaborationPolicy } & JsonMessage>(policyRes),
    readJson<{ items?: CollaborationInboxItem[] } & JsonMessage>(inboxRes),
    readJson<{ shares?: CollaborationShareInvitation[] } & JsonMessage>(sharesRes)
  ]);

  if (!policyRes.ok) {
    throw new CollaborationRouteError(
      failureMessage(policyRes, policyPayload, 'Supplier collaboration policy load failed'),
      policyPayload.errorCode
    );
  }
  if (!inboxRes.ok) {
    throw new CollaborationRouteError(
      failureMessage(inboxRes, inboxPayload, 'Supplier inbox load failed'),
      inboxPayload.errorCode
    );
  }
  if (!sharesRes.ok) {
    throw new CollaborationRouteError(
      failureMessage(sharesRes, sharesPayload, 'Sent shares load failed'),
      sharesPayload.errorCode
    );
  }

  let partners: CollaborationPartner[] = [];
  let contacts: CollaborationContact[] = [];
  let instances: CollaborationInstance[] = [];
  let principalChangeRequests: CollaborationPrincipalContactChangeRequest[] = [];
  let partnerTemplateReplyTos: CollaborationPartnerTemplateReplyTo[] = [];
  let partnerTemplateAuthorisations: CollaborationPartnerTemplateAuthorisation[] = [];
  let knownTemplateKeys: string[] = [];

  if (canManage || canInitiateShare) {
    // Tracker #121: a non-admin receives supplier names only, to label their
    // collaborations; contacts (supplier e-mail addresses) stay admin-only.
    const partnersRes = await fetch(`/api/collaboration/partners?${params.toString()}`);
    if (!partnersRes.ok) throw new Error(`Partners load failed (${partnersRes.status})`);
    const partnersPayload = await readJson<{ partners?: CollaborationPartner[] }>(partnersRes);
    partners = Array.isArray(partnersPayload.partners) ? partnersPayload.partners : [];
  }

  if (canManage) {
    const contactsRes = await fetch(`/api/collaboration/contacts?${params.toString()}`);
    if (!contactsRes.ok) throw new Error(`Contacts load failed (${contactsRes.status})`);
    const contactsPayload = await readJson<{ contacts?: CollaborationContact[] }>(contactsRes);
    contacts = Array.isArray(contactsPayload.contacts) ? contactsPayload.contacts : [];

    const templateReplyToRes = await fetch(`/api/collaboration/partner-template-reply-to?${params.toString()}`);
    if (!templateReplyToRes.ok) throw new Error(`Supplier/template reply-to load failed (${templateReplyToRes.status})`);
    const templateReplyToPayload = await readJson<{ overrides?: CollaborationPartnerTemplateReplyTo[] }>(templateReplyToRes);
    partnerTemplateReplyTos = Array.isArray(templateReplyToPayload.overrides) ? templateReplyToPayload.overrides : [];

    const templateAuthorisationRes = await fetch(`/api/collaboration/partner-template-authorisation?${params.toString()}`);
    if (!templateAuthorisationRes.ok) throw new Error(`Supplier/template authorisations load failed (${templateAuthorisationRes.status})`);
    const templateAuthorisationPayload = await readJson<{ authorisations?: CollaborationPartnerTemplateAuthorisation[] }>(templateAuthorisationRes);
    partnerTemplateAuthorisations = Array.isArray(templateAuthorisationPayload.authorisations)
      ? templateAuthorisationPayload.authorisations
      : [];

    // Template-key picker suggestions (tracker #62 fix 3a) — best-effort:
    // a failure here degrades the forms back to plain free text.
    try {
      const templateKeysRes = await fetch(`/api/collaboration/template-keys?${params.toString()}`);
      if (templateKeysRes.ok) {
        const templateKeysPayload = await readJson<{ templateKeys?: string[] }>(templateKeysRes);
        knownTemplateKeys = Array.isArray(templateKeysPayload.templateKeys)
          ? templateKeysPayload.templateKeys.filter((key): key is string => typeof key === 'string')
          : [];
      }
    } catch {
      knownTemplateKeys = [];
    }

    const instancesRes = await fetch(`/api/collaboration/instances?${params.toString()}`);
    if (!instancesRes.ok) throw new Error(`Instances load failed (${instancesRes.status})`);
    const instancesPayload = await readJson<{ instances?: CollaborationInstance[] }>(instancesRes);
    instances = Array.isArray(instancesPayload.instances) ? instancesPayload.instances : [];

    const principalRes = await fetch(`/api/collaboration/principal-contact-change-requests?${params.toString()}`);
    if (!principalRes.ok) {
      throw new Error(`Principal contact change requests load failed (${principalRes.status})`);
    }
    const principalPayload = await readJson<{ requests?: CollaborationPrincipalContactChangeRequest[] }>(principalRes);
    principalChangeRequests = Array.isArray(principalPayload.requests) ? principalPayload.requests : [];
  }

  return {
    policy: policyPayload.policy ?? null,
    inbox: Array.isArray(inboxPayload.items) ? inboxPayload.items : [],
    shares: Array.isArray(sharesPayload.shares) ? sharesPayload.shares : [],
    partners,
    contacts,
    instances,
    principalChangeRequests,
    partnerTemplateReplyTos,
    partnerTemplateAuthorisations,
    knownTemplateKeys
  };
};

export const updateInboundCustomerRelationshipStatus = async ({
  tenantId,
  inboundCustomerRelationshipId,
  status
}: {
  tenantId: string;
  inboundCustomerRelationshipId: string;
  status: Extract<CollaborationInboundCustomerRelationshipStatus, 'accepted' | 'blocked'>;
}): Promise<CollaborationInboundCustomerRelationship> => {
  const response = await fetch(
    `/api/collaboration/inbound-customers/${encodeURIComponent(inboundCustomerRelationshipId)}/status`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, status })
    }
  );
  const payload = await readJson<{ relationship?: CollaborationInboundCustomerRelationship } & JsonMessage>(response);
  if (!response.ok || !payload.relationship) {
    throw new Error(failureMessage(response, payload, 'Inbound customer relationship update failed'));
  }
  return payload.relationship;
};

export const updateInboundCustomerRelationshipDisplayName = async ({
  tenantId,
  inboundCustomerRelationshipId,
  sourceCustomerDisplayName,
  expectedRowVersion
}: {
  tenantId: string;
  inboundCustomerRelationshipId: string;
  sourceCustomerDisplayName: string;
  expectedRowVersion: string;
}): Promise<CollaborationInboundCustomerRelationship> => {
  const response = await fetch(
    `/api/collaboration/inbound-customers/${encodeURIComponent(inboundCustomerRelationshipId)}/display-name`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, sourceCustomerDisplayName, expectedRowVersion })
    }
  );
  const payload = await readJson<{ relationship?: CollaborationInboundCustomerRelationship } & JsonMessage>(response);
  if (response.status === 409) {
    throw new Error('Another admin updated this customer display name. Reload to see the latest name and try again.');
  }
  if (!response.ok || !payload.relationship) {
    throw new Error(failureMessage(response, payload, 'Inbound customer display name update failed'));
  }
  return payload.relationship;
};

export const updateInboxItemTriageStatus = async ({
  tenantId,
  inboxItemId,
  status
}: {
  tenantId: string;
  inboxItemId: string;
  status: 'accepted' | 'rejected' | 'archived';
}): Promise<CollaborationInboxItem> => {
  const response = await fetch(`/api/collaboration/inbox/${encodeURIComponent(inboxItemId)}/triage`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId, status })
  });
  const payload = await readJson<{ item?: CollaborationInboxItem } & JsonMessage>(response);
  if (!response.ok || !payload.item) {
    throw new Error(failureMessage(response, payload, 'Inbox item triage failed'));
  }
  return payload.item;
};

export const pullInboxItemLatestBaseline = async ({
  tenantId,
  inboxItemId
}: {
  tenantId: string;
  inboxItemId: string;
}): Promise<CollaborationInboxItem> => {
  const response = await fetch(`/api/collaboration/inbox/${encodeURIComponent(inboxItemId)}/pull-update`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId })
  });
  const payload = await readJson<{ item?: CollaborationInboxItem } & JsonMessage>(response);
  if (!response.ok || !payload.item) {
    throw new Error(failureMessage(response, payload, 'Inbox baseline pull failed'));
  }
  return payload.item;
};

export const acknowledgeInboxItemBaseline = async ({
  tenantId,
  inboxItemId
}: {
  tenantId: string;
  inboxItemId: string;
}): Promise<CollaborationInboxItem> => {
  const response = await fetch(`/api/collaboration/inbox/${encodeURIComponent(inboxItemId)}/baseline/acknowledge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId })
  });
  const payload = await readJson<{ item?: CollaborationInboxItem } & JsonMessage>(response);
  if (!response.ok || !payload.item) {
    throw new Error(failureMessage(response, payload, 'Inbox baseline acknowledgement failed'));
  }
  return payload.item;
};

export const saveCollaborationPartnerTemplateReplyTo = async ({
  tenantId,
  form
}: {
  tenantId: string;
  form: CollaborationPartnerTemplateReplyToFormState;
}): Promise<CollaborationPartnerTemplateReplyTo> => {
  const response = await fetch('/api/collaboration/partner-template-reply-to', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tenantId,
      partnerId: form.partnerId,
      templateKey: form.templateKey,
      replyToEmail: form.replyToEmail
    })
  });
  const payload = await readJson<{ override?: CollaborationPartnerTemplateReplyTo } & JsonMessage>(response);
  if (!response.ok || !payload.override?.partnerTemplateReplyToId) {
    throw new Error(failureMessage(response, payload, 'Supplier/template reply-to save failed'));
  }
  return payload.override;
};

export const deleteCollaborationPartnerTemplateReplyTo = async ({
  tenantId,
  overrideId
}: {
  tenantId: string;
  overrideId: string;
}): Promise<void> => {
  const params = new URLSearchParams({ tenantId });
  const response = await fetch(
    `/api/collaboration/partner-template-reply-to/${encodeURIComponent(overrideId)}?${params.toString()}`,
    { method: 'DELETE' }
  );
  const payload = await readJson<JsonMessage>(response);
  if (!response.ok) {
    throw new Error(failureMessage(response, payload, 'Supplier/template reply-to delete failed'));
  }
};

export const saveCollaborationPartnerTemplateAuthorisation = async ({
  tenantId,
  form
}: {
  tenantId: string;
  form: CollaborationPartnerTemplateAuthorisationFormState;
}): Promise<CollaborationPartnerTemplateAuthorisation> => {
  const response = await fetch('/api/collaboration/partner-template-authorisation', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tenantId,
      partnerId: form.partnerId,
      contactId: form.contactId || null,
      templateKey: form.templateKey
    })
  });
  const payload = await readJson<{ authorisation?: CollaborationPartnerTemplateAuthorisation } & JsonMessage>(response);
  if (!response.ok || !payload.authorisation?.partnerTemplateAuthorisationId) {
    throw new Error(failureMessage(response, payload, 'Supplier/template authorisation save failed'));
  }
  return payload.authorisation;
};

export const deleteCollaborationPartnerTemplateAuthorisation = async ({
  tenantId,
  authorisationId
}: {
  tenantId: string;
  authorisationId: string;
}): Promise<void> => {
  const params = new URLSearchParams({ tenantId });
  const response = await fetch(
    `/api/collaboration/partner-template-authorisation/${encodeURIComponent(authorisationId)}?${params.toString()}`,
    { method: 'DELETE' }
  );
  const payload = await readJson<JsonMessage>(response);
  if (!response.ok) {
    throw new Error(failureMessage(response, payload, 'Supplier/template authorisation delete failed'));
  }
};

export const saveCollaborationPolicy = async ({
  tenantId,
  policyForm
}: {
  tenantId: string;
  policyForm: CollaborationPolicyFormState;
}): Promise<CollaborationPolicy | null> => {
  const response = await fetch('/api/collaboration/policy', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId, ...policyForm })
  });
  const payload = await readJson<{ policy?: CollaborationPolicy } & JsonMessage>(response);
  if (!response.ok) {
    throw new Error(failureMessage(response, payload, 'Policy save failed'));
  }
  return payload.policy ?? null;
};

export const createCollaborationContact = async ({
  tenantId,
  contactForm
}: {
  tenantId: string;
  contactForm: CollaborationContactFormState;
}): Promise<void> => {
  const response = await fetch('/api/collaboration/contacts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tenantId,
      partnerId: contactForm.partnerId || undefined,
      email: contactForm.email,
      displayName: contactForm.displayName || undefined,
      contactType: contactForm.contactType,
      isPrincipalContact: contactForm.isPrincipalContact,
      status: 'active'
    })
  });
  const payload = await readJson<JsonMessage>(response);
  if (!response.ok) {
    throw new Error(failureMessage(response, payload, 'Contact create failed'));
  }
};

export const createCollaborationPartner = async ({
  tenantId,
  partnerForm
}: {
  tenantId: string;
  partnerForm: {
    displayName: string;
    primaryDomain: string;
    externalSupplierReference: string;
    defaultReplyToEmail: string;
    footprintMode: string;
    supplierCategory?: string;
    riskTier?: string;
  };
}): Promise<CollaborationPartner> => {
  const response = await fetch('/api/collaboration/partners', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tenantId,
      displayName: partnerForm.displayName,
      primaryDomain: partnerForm.primaryDomain || undefined,
      externalSupplierReference: partnerForm.externalSupplierReference || undefined,
      defaultReplyToEmail: partnerForm.defaultReplyToEmail || undefined,
      footprintMode: partnerForm.footprintMode,
      supplierCategory: partnerForm.supplierCategory || undefined,
      riskTier: partnerForm.riskTier || undefined,
      status: 'draft'
    })
  });
  const payload = await readJson<{ partner?: CollaborationPartner } & JsonMessage>(response);
  if (!response.ok) {
    throw new Error(failureMessage(response, payload, 'Partner create failed'));
  }
  if (!payload.partner) {
    throw new Error('Partner create succeeded without returning the supplier record.');
  }
  return payload.partner;
};

export const deleteCollaborationPartner = async ({
  tenantId,
  partnerId
}: {
  tenantId: string;
  partnerId: string;
}): Promise<void> => {
  const params = new URLSearchParams({ tenantId });
  const response = await fetch(
    `/api/collaboration/partners/${encodeURIComponent(partnerId)}?${params.toString()}`,
    { method: 'DELETE' }
  );
  const payload = await readJson<JsonMessage>(response);
  if (!response.ok) {
    throw new CollaborationRouteError(
      failureMessage(response, payload, 'Supplier removal failed'),
      payload.errorCode
    );
  }
};

export const previewSupplierImport = async ({
  tenantId,
  file
}: {
  tenantId: string;
  file: File;
}): Promise<SupplierImportBatchSnapshot> => {
  const body = new FormData();
  body.append('tenantId', tenantId);
  body.append('file', file);
  const response = await fetch('/api/collaboration/supplier-imports/preview', {
    method: 'POST',
    body
  });
  const payload = await readJson<{ batch?: SupplierImportBatchSnapshot } & JsonMessage>(response);
  if (!response.ok || !payload.batch?.importBatchId) {
    throw new Error(failureMessage(response, payload, 'Supplier import preview failed'));
  }
  return payload.batch;
};

export const commitSupplierImport = async ({
  tenantId,
  importBatchId,
  rowIds
}: {
  tenantId: string;
  importBatchId: string;
  rowIds: string[];
}): Promise<SupplierImportBatchSnapshot> => {
  const response = await fetch(`/api/collaboration/supplier-imports/${encodeURIComponent(importBatchId)}/commit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId, rowIds })
  });
  const payload = await readJson<{ batch?: SupplierImportBatchSnapshot } & JsonMessage>(response);
  if (!response.ok || !payload.batch?.importBatchId) {
    throw new Error(failureMessage(response, payload, 'Supplier import commit failed'));
  }
  return payload.batch;
};

export const issueCollaborationPartnerAdminInvite = async ({
  tenantId,
  partnerId
}: {
  tenantId: string;
  partnerId: string;
}): Promise<{ inviteUrl: string; invitedEmail: string; expiresAt: string }> => {
  const response = await fetch(`/api/collaboration/partners/${encodeURIComponent(partnerId)}/admin-invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId })
  });
  const payload = await readJson<{ inviteUrl?: string; invitedEmail?: string; expiresAt?: string } & JsonMessage>(response);
  if (!response.ok || !payload.inviteUrl || !payload.invitedEmail || !payload.expiresAt) {
    throw new Error(failureMessage(response, payload, 'Partner admin invite failed'));
  }
  return {
    inviteUrl: payload.inviteUrl,
    invitedEmail: payload.invitedEmail,
    expiresAt: payload.expiresAt
  };
};

/**
 * Supplier Profile drill-in aggregate (tracker #7): partner detail,
 * admin-invite history, and the linked supplier organisation's
 * registration state and the partner-scoped companion data required by the
 * page. A single capability-gated read avoids loading the collaboration
 * inbox or synchronising instances just to render one supplier.
 */
export const loadCollaborationPartnerProfile = async ({
  tenantId,
  partnerId
}: {
  tenantId: string;
  partnerId: string;
}): Promise<CollaborationPartnerProfile> => {
  const params = new URLSearchParams({ tenantId });
  const response = await fetch(
    `/api/collaboration/partners/${encodeURIComponent(partnerId)}/profile?${params.toString()}`
  );
  const payload = await readJson<Partial<CollaborationPartnerProfile> & JsonMessage>(response);
  if (!response.ok || !payload.partner) {
    throw new CollaborationRouteError(
      failureMessage(response, payload, 'Supplier profile load failed'),
      payload.errorCode
    );
  }
  return {
    partner: payload.partner,
    adminInvites: Array.isArray(payload.adminInvites) ? payload.adminInvites : [],
    supplierOrganisation: payload.supplierOrganisation ?? null,
    contacts: Array.isArray(payload.contacts) ? payload.contacts : [],
    templateAuthorisations: Array.isArray(payload.templateAuthorisations) ? payload.templateAuthorisations : [],
    templateReplyTos: Array.isArray(payload.templateReplyTos) ? payload.templateReplyTos : [],
    recentShares: Array.isArray(payload.recentShares) ? payload.recentShares : [],
    hasInstanceDefaultReplyTo: payload.hasInstanceDefaultReplyTo === true
  };
};

export const updateCollaborationPartner = async ({
  tenantId,
  partner,
  status
}: {
  tenantId: string;
  partner: CollaborationPartner;
  status: CollaborationPartner['status'];
}): Promise<void> => {
  const response = await fetch('/api/collaboration/partners', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tenantId,
      partnerId: partner.partnerId,
      displayName: partner.displayName,
      primaryDomain: partner.primaryDomain || undefined,
      externalSupplierReference: partner.externalSupplierReference || undefined,
      defaultReplyToEmail: partner.defaultReplyToEmail || undefined,
      footprintMode: partner.footprintMode,
      supplierCategory: partner.supplierCategory ?? null,
      riskTier: partner.riskTier ?? null,
      status
    })
  });
  const payload = await readJson<JsonMessage>(response);
  if (!response.ok) {
    throw new CollaborationRouteError(
      failureMessage(response, payload, 'Partner update failed'),
      payload.errorCode
    );
  }
};

export const updateCollaborationContact = async ({
  tenantId,
  contact,
  status
}: {
  tenantId: string;
  contact: CollaborationContact;
  status: CollaborationContact['status'];
}): Promise<void> => {
  const response = await fetch('/api/collaboration/contacts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tenantId,
      partnerId: contact.partnerId || undefined,
      email: contact.emailNormalized,
      displayName: contact.displayName ?? undefined,
      contactType: contact.contactType,
      isPrincipalContact: contact.isPrincipalContact,
      status
    })
  });
  const payload = await readJson<JsonMessage>(response);
  if (!response.ok) {
    throw new Error(failureMessage(response, payload, 'Contact update failed'));
  }
};

export const assignCollaborationContact = async ({
  tenantId,
  contactId,
  instanceId
}: {
  tenantId: string;
  contactId: string;
  instanceId: string;
}): Promise<void> => {
  const response = await fetch(`/api/collaboration/contacts/${encodeURIComponent(contactId)}/instances`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId, instanceId })
  });
  const payload = await readJson<JsonMessage>(response);
  if (!response.ok) {
    throw new Error(failureMessage(response, payload, 'Assignment failed'));
  }
};

export const reviewCollaborationPrincipalContactChangeRequest = async ({
  tenantId,
  requestId,
  action
}: {
  tenantId: string;
  requestId: string;
  action: 'approve' | 'reject';
}): Promise<void> => {
  const response = await fetch(
    `/api/collaboration/principal-contact-change-requests/${encodeURIComponent(requestId)}/${action}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId })
    }
  );
  const payload = await readJson<JsonMessage>(response);
  if (!response.ok) {
    throw new Error(failureMessage(response, payload, `Principal contact change ${action} failed`));
  }
};

export const revokeCollaborationShare = async ({
  tenantId,
  shareInvitationId
}: {
  tenantId: string;
  shareInvitationId: string;
}): Promise<void> => {
  const response = await fetch(`/api/collaboration/share/${encodeURIComponent(shareInvitationId)}/revoke`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId })
  });
  const payload = await readJson<JsonMessage>(response);
  if (!response.ok) {
    throw new Error(failureMessage(response, payload, 'Share revoke failed'));
  }
};

export const updateCollaborationInstanceSettings = async ({
  tenantId,
  instance,
  acceptsCollaboration,
  collaborationOutbound,
  collaborationInbound,
  defaultCollaborationReplyToEmail
}: {
  tenantId: string;
  instance: CollaborationInstance;
  acceptsCollaboration: boolean;
  collaborationOutbound: boolean;
  collaborationInbound: boolean;
  defaultCollaborationReplyToEmail?: string | null;
}): Promise<CollaborationInstance> => {
  const response = await fetch(`/api/collaboration/instances/${encodeURIComponent(instance.instanceId)}/settings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tenantId,
      acceptsCollaboration,
      collaborationOutbound,
      collaborationInbound,
      defaultCollaborationReplyToEmail:
        defaultCollaborationReplyToEmail ?? instance.defaultCollaborationReplyToEmail ?? undefined
    })
  });
  const payload = await readJson<{ instance?: CollaborationInstance } & JsonMessage>(response);
  if (!response.ok || !payload.instance?.instanceId) {
    throw new Error(failureMessage(response, payload, 'Instance settings update failed'));
  }
  return payload.instance;
};

export const revokeCollaborationParentBinding = async ({
  tenantId,
  instance,
  parentBindingId
}: {
  tenantId: string;
  instance: CollaborationInstance;
  parentBindingId: string;
}): Promise<CollaborationInstance> => {
  const response = await fetch(
    `/api/collaboration/instances/${encodeURIComponent(instance.instanceId)}/parent-bindings/${encodeURIComponent(parentBindingId)}/revoke`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId })
    }
  );
  const payload = await readJson<{ instance?: CollaborationInstance } & JsonMessage>(response);
  if (!response.ok || !payload.instance?.instanceId) {
    throw new Error(failureMessage(response, payload, 'Parent binding revoke failed'));
  }
  return payload.instance;
};

export const loadCollaborationShareThread = async ({
  tenantId,
  shareInvitationId
}: {
  tenantId: string;
  shareInvitationId: string;
}): Promise<CollaborationThreadSnapshot> => {
  const params = new URLSearchParams({ tenantId });
  const response = await fetch(
    `/api/collaboration/shares/${encodeURIComponent(shareInvitationId)}/thread?${params.toString()}`
  );
  const payload = await readJson<CollaborationThreadSnapshot & JsonMessage>(response);
  if (!response.ok || !payload.thread) {
    throw new Error(failureMessage(response, payload, 'Thread load failed'));
  }
  return payload;
};

export const loadCollaborationRecipientThread = async ({
  tenantId,
  inboxItemId
}: {
  tenantId: string;
  inboxItemId: string;
}): Promise<CollaborationThreadSnapshot> => {
  const params = new URLSearchParams({ tenantId });
  const response = await fetch(
    `/api/collaboration/recipient/inbox/${encodeURIComponent(inboxItemId)}/thread?${params.toString()}`
  );
  const payload = await readJson<CollaborationThreadSnapshot & JsonMessage>(response);
  if (!response.ok || !payload.thread) {
    throw new Error(failureMessage(response, payload, 'Recipient thread load failed'));
  }
  return payload;
};

export const postCollaborationShareThreadMessage = async ({
  tenantId,
  shareInvitationId,
  body
}: {
  tenantId: string;
  shareInvitationId: string;
  body: string;
}): Promise<void> => {
  const response = await fetch(
    `/api/collaboration/shares/${encodeURIComponent(shareInvitationId)}/thread/messages`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, body })
    }
  );
  const payload = await readJson<{ message?: { messageId?: string } } & JsonMessage>(response);
  if (!response.ok || !payload.message || typeof payload.message !== 'object' || !payload.message.messageId) {
    throw new Error(failureMessage(response, payload, 'Thread message post failed'));
  }
};

export interface CollaborationThreadMessageTranslationResult {
  translation: CollaborationThreadMessageTranslation;
  /** False when the platform translated (and metered) but the Portal could not store the rendering. */
  persisted: boolean;
  /** True when a current rendering already existed and no new translation was metered. */
  reused: boolean;
  warning?: string;
}

const readTranslationResult = async (response: Response): Promise<CollaborationThreadMessageTranslationResult> => {
  const payload = await readJson<{
    translation?: CollaborationThreadMessageTranslation;
    persisted?: boolean;
    reused?: boolean;
    warning?: string;
  } & JsonMessage>(response);
  if (!response.ok || !payload.translation || typeof payload.translation.translatedBody !== 'string') {
    throw new Error(failureMessage(response, payload, 'Thread message translation failed'));
  }
  return {
    translation: payload.translation,
    persisted: payload.persisted !== false,
    reused: payload.reused === true,
    warning: typeof payload.warning === 'string' ? payload.warning : undefined
  };
};

export const translateCollaborationShareThreadMessage = async ({
  tenantId,
  shareInvitationId,
  messageId,
  targetLanguage,
  force
}: {
  tenantId: string;
  shareInvitationId: string;
  messageId: string;
  targetLanguage: string;
  /** Translate again even though a current rendering exists (meters again). */
  force?: boolean;
}): Promise<CollaborationThreadMessageTranslationResult> => {
  const response = await fetch(
    `/api/collaboration/shares/${encodeURIComponent(shareInvitationId)}/thread/messages/${encodeURIComponent(messageId)}/translate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, targetLanguage, force: force === true })
    }
  );
  return readTranslationResult(response);
};

export const translateCollaborationRecipientThreadMessage = async ({
  tenantId,
  inboxItemId,
  messageId,
  targetLanguage,
  force
}: {
  tenantId: string;
  inboxItemId: string;
  messageId: string;
  targetLanguage: string;
  force?: boolean;
}): Promise<CollaborationThreadMessageTranslationResult> => {
  const response = await fetch(
    `/api/collaboration/recipient/inbox/${encodeURIComponent(inboxItemId)}/thread/messages/${encodeURIComponent(messageId)}/translate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, targetLanguage, force: force === true })
    }
  );
  return readTranslationResult(response);
};

export const postCollaborationRecipientThreadMessage = async ({
  tenantId,
  inboxItemId,
  body
}: {
  tenantId: string;
  inboxItemId: string;
  body: string;
}): Promise<void> => {
  const response = await fetch(
    `/api/collaboration/recipient/inbox/${encodeURIComponent(inboxItemId)}/thread/messages`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, body })
    }
  );
  const payload = await readJson<{ message?: { messageId?: string } } & JsonMessage>(response);
  if (!response.ok || !payload.message || typeof payload.message !== 'object' || !payload.message.messageId) {
    throw new Error(failureMessage(response, payload, 'Recipient thread message post failed'));
  }
};

/**
 * Uploads one returned file on its own (file pipeline P1): the body is the
 * file itself, its name and type travel in the x-edinshall-upload header.
 * Returns the upload id a sync package then names.
 */
export const uploadCollaborationRecipientFile = async ({
  tenantId,
  inboxItemId,
  file
}: {
  tenantId: string;
  inboxItemId: string;
  file: File;
}): Promise<{ uploadId: string }> => {
  const response = await fetch(`/api/collaboration/recipient/inbox/${encodeURIComponent(inboxItemId)}/uploads`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/octet-stream',
      'x-edinshall-upload': encodeURIComponent(JSON.stringify({ tenantId, fileName: file.name, contentType: file.type || null }))
    },
    body: file
  });
  const payload = await readJson<{ upload?: { uploadId?: string } } & JsonMessage>(response);
  if (!response.ok || !payload.upload?.uploadId) {
    throw new Error(failureMessage(response, payload, `Uploading ${file.name} failed`));
  }
  return { uploadId: payload.upload.uploadId };
};

export const submitCollaborationRecipientSyncPackage = async ({
  tenantId,
  inboxItemId,
  workItemId,
  packageType,
  proposal,
  originVersionToken,
  uploads,
  submissionMode
}: {
  tenantId: string;
  inboxItemId: string;
  workItemId: string;
  packageType: 'field_update' | 'action_proposal' | 'request_close';
  proposal: Record<string, unknown>;
  originVersionToken: string;
  /** Files already sent with `uploadCollaborationRecipientFile`. */
  uploads: Array<{ uploadId: string; isRequiredForReview: boolean }>;
  submissionMode?: 'draft' | 'submit';
}): Promise<void> => {
  const response = await fetch(
    `/api/collaboration/recipient/inbox/${encodeURIComponent(inboxItemId)}/thread/sync-packages`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tenantId,
        workItemId,
        packageType,
        proposal,
        originVersionToken,
        uploads,
        submissionMode
      })
    }
  );
  const payload = await readJson<{ syncPackage?: { syncPackageId?: string } } & JsonMessage>(response);
  if (!response.ok || !payload.syncPackage || typeof payload.syncPackage !== 'object' || !payload.syncPackage.syncPackageId) {
    throw new Error(failureMessage(response, payload, 'Recipient sync package failed'));
  }
};

export const withdrawCollaborationRecipientSyncPackage = async ({
  tenantId,
  inboxItemId,
  syncPackageId,
  reason
}: {
  tenantId: string;
  inboxItemId: string;
  syncPackageId: string;
  reason: string;
}): Promise<void> => {
  const response = await fetch(
    `/api/collaboration/recipient/inbox/${encodeURIComponent(inboxItemId)}/thread/sync-packages/${encodeURIComponent(syncPackageId)}/withdraw`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, reason })
    }
  );
  const payload = await readJson<{ syncPackage?: { syncPackageId?: string } } & JsonMessage>(response);
  if (!response.ok || !payload.syncPackage || typeof payload.syncPackage !== 'object' || !payload.syncPackage.syncPackageId) {
    throw new Error(failureMessage(response, payload, 'Covered-tenant sync package withdrawal failed'));
  }
};

export const acknowledgeCollaborationRecipientSourceUpdate = async ({
  tenantId,
  inboxItemId,
  syncPackageId,
  rebasedDraft
}: {
  tenantId: string;
  inboxItemId: string;
  syncPackageId: string;
  rebasedDraft?: {
    summary?: string;
    fieldUpdates?: Array<{
      fieldKey: CollaborationEditableFieldKey;
      proposedValue?: string | null;
    }>;
  };
}): Promise<void> => {
  const response = await fetch(
    `/api/collaboration/recipient/inbox/${encodeURIComponent(inboxItemId)}/thread/sync-packages/${encodeURIComponent(syncPackageId)}/acknowledge-source-update`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, rebasedDraft })
    }
  );
  const payload = await readJson<{ syncPackage?: { syncPackageId?: string } } & JsonMessage>(response);
  if (!response.ok || !payload.syncPackage || typeof payload.syncPackage !== 'object' || !payload.syncPackage.syncPackageId) {
    throw new Error(failureMessage(response, payload, 'Covered-tenant source update acknowledgement failed'));
  }
};

export const updateCollaborationWorkItemState = async ({
  tenantId,
  shareInvitationId,
  workItemId,
  targetState,
  reason
}: {
  tenantId: string;
  shareInvitationId: string;
  workItemId: string;
  targetState: 'active' | 'recalled';
  reason: string;
}): Promise<{ stalePackageCount?: number }> => {
  const response = await fetch(
    `/api/collaboration/shares/${encodeURIComponent(shareInvitationId)}/thread/work-items/${encodeURIComponent(workItemId)}/state`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, targetState, reason })
    }
  );
  const payload = await readJson<{ workItem?: { workItemId?: string }; stalePackageCount?: number } & JsonMessage>(response);
  if (!response.ok || !payload.workItem || typeof payload.workItem !== 'object' || !payload.workItem.workItemId) {
    throw new Error(failureMessage(response, payload, 'Work item state update failed'));
  }
  return { stalePackageCount: payload.stalePackageCount };
};

export const withdrawCollaborationReturnedActionProposal = async ({
  tenantId,
  inboxItemId,
  returnedActionProposalId
}: {
  tenantId: string;
  inboxItemId: string;
  returnedActionProposalId: string;
}): Promise<void> => {
  const response = await fetch(
    `/api/collaboration/recipient/inbox/${encodeURIComponent(inboxItemId)}/thread/returned-actions/${encodeURIComponent(returnedActionProposalId)}/withdraw`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId })
    }
  );
  const payload = await readJson<{ returnedActionProposal?: { returnedActionProposalId?: string } } & JsonMessage>(
    response
  );
  if (
    !response.ok ||
    !payload.returnedActionProposal ||
    typeof payload.returnedActionProposal !== 'object' ||
    !payload.returnedActionProposal.returnedActionProposalId
  ) {
    throw new Error(failureMessage(response, payload, 'Recipient Action withdrawal failed'));
  }
};

export const reviewCollaborationSyncPackage = async ({
  tenantId,
  shareInvitationId,
  syncPackageId,
  decision,
  reviewNote
}: {
  tenantId: string;
  shareInvitationId: string;
  syncPackageId: string;
  decision: 'accepted' | 'rejected';
  reviewNote: string;
}): Promise<{
  httpStatus: number;
  status?: CollaborationThreadSyncPackage['status'];
  message?: string;
}> => {
  const response = await fetch(
    `/api/collaboration/shares/${encodeURIComponent(shareInvitationId)}/thread/sync-packages/${encodeURIComponent(syncPackageId)}/review`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, decision, reviewNote })
    }
  );
  const payload = await readJson<{ syncPackage?: { syncPackageId?: string; status?: CollaborationThreadSyncPackage['status'] } } & JsonMessage>(
    response
  );
  if (response.status === 409 && payload.syncPackage?.status === 'stale_conflict') {
    return {
      httpStatus: response.status,
      status: payload.syncPackage.status,
      message: failureMessage(response, payload, 'Sync package review failed')
    };
  }
  if (!response.ok || !payload.syncPackage || typeof payload.syncPackage !== 'object' || !payload.syncPackage.syncPackageId) {
    throw new Error(failureMessage(response, payload, 'Sync package review failed'));
  }
  return {
    httpStatus: response.status,
    status: payload.syncPackage.status
  };
};

export const withdrawCollaborationSyncPackage = async ({
  tenantId,
  shareInvitationId,
  syncPackageId,
  reason
}: {
  tenantId: string;
  shareInvitationId: string;
  syncPackageId: string;
  reason: string;
}): Promise<void> => {
  const response = await fetch(
    `/api/collaboration/shares/${encodeURIComponent(shareInvitationId)}/thread/sync-packages/${encodeURIComponent(syncPackageId)}/withdraw`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, reason })
    }
  );
  const payload = await readJson<{ syncPackage?: { syncPackageId?: string } } & JsonMessage>(response);
  if (!response.ok || !payload.syncPackage || typeof payload.syncPackage !== 'object' || !payload.syncPackage.syncPackageId) {
    throw new Error(failureMessage(response, payload, 'Sync package supersede failed'));
  }
};

export const refreshCollaborationWorkItemOriginVersion = async ({
  tenantId,
  shareInvitationId,
  workItemId,
  reason
}: {
  tenantId: string;
  shareInvitationId: string;
  workItemId: string;
  reason: string;
}): Promise<{ stalePackageCount?: number }> => {
  const response = await fetch(
    `/api/collaboration/shares/${encodeURIComponent(shareInvitationId)}/thread/work-items/${encodeURIComponent(workItemId)}/refresh-version`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, reason })
    }
  );
  const payload = await readJson<{ workItem?: { workItemId?: string }; stalePackageCount?: number } & JsonMessage>(response);
  if (!response.ok || !payload.workItem || typeof payload.workItem !== 'object' || !payload.workItem.workItemId) {
    throw new Error(failureMessage(response, payload, 'Work item version refresh failed'));
  }
  return { stalePackageCount: payload.stalePackageCount };
};

export const refreshAllCollaborationWorkItemOriginVersions = async ({
  tenantId,
  shareInvitationId,
  reason
}: {
  tenantId: string;
  shareInvitationId: string;
  reason: string;
}): Promise<{ refreshedWorkItemCount: number; stalePackageCount?: number }> => {
  const response = await fetch(
    `/api/collaboration/shares/${encodeURIComponent(shareInvitationId)}/thread/refresh-all-work-items`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, reason })
    }
  );
  const payload = await readJson<{ refreshedWorkItemCount?: number; stalePackageCount?: number } & JsonMessage>(
    response
  );
  if (!response.ok || typeof payload.refreshedWorkItemCount !== 'number') {
    throw new Error(failureMessage(response, payload, 'Bulk work item refresh failed'));
  }
  return {
    refreshedWorkItemCount: payload.refreshedWorkItemCount,
    stalePackageCount: payload.stalePackageCount
  };
};

export const releaseCollaborationArtifact = async ({
  tenantId,
  shareInvitationId,
  artifactId
}: {
  tenantId: string;
  shareInvitationId: string;
  artifactId: string;
}): Promise<void> => {
  const response = await fetch(
    `/api/collaboration/shares/${encodeURIComponent(shareInvitationId)}/thread/artifacts/${encodeURIComponent(artifactId)}/release`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId })
    }
  );
  const payload = await readJson<{ artifact?: { artifactId?: string } } & JsonMessage>(response);
  if (!response.ok || !payload.artifact || typeof payload.artifact !== 'object' || !payload.artifact.artifactId) {
    throw new Error(failureMessage(response, payload, 'Artifact release failed'));
  }
};

export const downloadCollaborationArtifact = async ({
  tenantId,
  shareInvitationId,
  artifactId,
  fileName
}: {
  tenantId: string;
  shareInvitationId: string;
  artifactId: string;
  fileName: string;
}): Promise<void> => {
  const response = await fetch(
    `/api/collaboration/shares/${encodeURIComponent(shareInvitationId)}/thread/artifacts/${encodeURIComponent(artifactId)}/download?tenantId=${encodeURIComponent(tenantId)}`,
    {
      method: 'GET'
    }
  );
  if (!response.ok) {
    let payload: JsonMessage = {};
    try {
      payload = await response.json();
    } catch {
      payload = {};
    }
    throw new Error(failureMessage(response, payload, 'Artifact download failed'));
  }
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName || 'collaboration-artifact';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    window.URL.revokeObjectURL(url);
  }
};

export const downloadRecipientCollaborationArtifact = async ({
  tenantId,
  inboxItemId,
  artifactId,
  fileName
}: {
  tenantId: string;
  inboxItemId: string;
  artifactId: string;
  fileName: string;
}): Promise<void> => {
  const response = await fetch(
    `/api/collaboration/recipient/inbox/${encodeURIComponent(inboxItemId)}/thread/artifacts/${encodeURIComponent(artifactId)}/download?tenantId=${encodeURIComponent(tenantId)}`,
    {
      method: 'GET'
    }
  );
  if (!response.ok) {
    let payload: JsonMessage = {};
    try {
      payload = await response.json();
    } catch {
      payload = {};
    }
    throw new Error(failureMessage(response, payload, 'Evidence download failed'));
  }
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName || 'customer-evidence';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    window.URL.revokeObjectURL(url);
  }
};

export const purgeCollaborationArtifactBinary = async ({
  tenantId,
  shareInvitationId,
  artifactId,
  reason
}: {
  tenantId: string;
  shareInvitationId: string;
  artifactId: string;
  reason: string;
}): Promise<{ purgedArtifactCount: number }> => {
  const response = await fetch(
    `/api/collaboration/shares/${encodeURIComponent(shareInvitationId)}/thread/artifacts/${encodeURIComponent(artifactId)}/purge-binary`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, reason })
    }
  );
  const payload = await readJson<{ purgedArtifactCount?: number } & JsonMessage>(response);
  if (!response.ok || typeof payload.purgedArtifactCount !== 'number') {
    throw new Error(failureMessage(response, payload, 'Artifact purge failed'));
  }
  return { purgedArtifactCount: payload.purgedArtifactCount };
};

export const purgeCollaborationSyncPackageArtifacts = async ({
  tenantId,
  shareInvitationId,
  syncPackageId,
  reason
}: {
  tenantId: string;
  shareInvitationId: string;
  syncPackageId: string;
  reason: string;
}): Promise<{ purgedArtifactCount: number }> => {
  const response = await fetch(
    `/api/collaboration/shares/${encodeURIComponent(shareInvitationId)}/thread/sync-packages/${encodeURIComponent(syncPackageId)}/purge-artifacts`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, reason })
    }
  );
  const payload = await readJson<{ purgedArtifactCount?: number } & JsonMessage>(response);
  if (!response.ok || typeof payload.purgedArtifactCount !== 'number') {
    throw new Error(failureMessage(response, payload, 'Sync package artifact purge failed'));
  }
  return { purgedArtifactCount: payload.purgedArtifactCount };
};

export const purgeCollaborationThreadArtifacts = async ({
  tenantId,
  shareInvitationId,
  reason
}: {
  tenantId: string;
  shareInvitationId: string;
  reason: string;
}): Promise<{ purgedArtifactCount: number }> => {
  const response = await fetch(
    `/api/collaboration/shares/${encodeURIComponent(shareInvitationId)}/thread/purge-artifacts`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, reason })
    }
  );
  const payload = await readJson<{ purgedArtifactCount?: number } & JsonMessage>(response);
  if (!response.ok || typeof payload.purgedArtifactCount !== 'number') {
    throw new Error(failureMessage(response, payload, 'Thread artifact purge failed'));
  }
  return { purgedArtifactCount: payload.purgedArtifactCount };
};

export const purgeCollaborationTenantArtifacts = async ({
  tenantId,
  reason
}: {
  tenantId: string;
  reason: string;
}): Promise<{ purgedArtifactCount: number }> => {
  const response = await fetch('/api/collaboration/artifacts/purge-all', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId, reason })
  });
  const payload = await readJson<{ purgedArtifactCount?: number } & JsonMessage>(response);
  if (!response.ok || typeof payload.purgedArtifactCount !== 'number') {
    throw new Error(failureMessage(response, payload, 'Tenant artifact purge failed'));
  }
  return { purgedArtifactCount: payload.purgedArtifactCount };
};

export const purgeCollaborationInstanceArtifacts = async ({
  tenantId,
  instanceId,
  reason
}: {
  tenantId: string;
  instanceId: string;
  reason: string;
}): Promise<{ purgedArtifactCount: number }> => {
  const response = await fetch(`/api/collaboration/instances/${encodeURIComponent(instanceId)}/purge-artifacts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId, reason })
  });
  const payload = await readJson<{ purgedArtifactCount?: number } & JsonMessage>(response);
  if (!response.ok || typeof payload.purgedArtifactCount !== 'number') {
    throw new Error(failureMessage(response, payload, 'Instance artifact purge failed'));
  }
  return { purgedArtifactCount: payload.purgedArtifactCount };
};

export const decideCollaborationReturnedActionProposal = async ({
  tenantId,
  shareInvitationId,
  returnedActionProposalId,
  decision
}: {
  tenantId: string;
  shareInvitationId: string;
  returnedActionProposalId: string;
  decision: 'adopted' | 'declined';
}): Promise<void> => {
  const response = await fetch(
    `/api/collaboration/shares/${encodeURIComponent(shareInvitationId)}/thread/returned-actions/${encodeURIComponent(returnedActionProposalId)}/decision`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, decision })
    }
  );
  const payload = await readJson<{ returnedActionProposal?: { returnedActionProposalId?: string } } & JsonMessage>(
    response
  );
  if (
    !response.ok ||
    !payload.returnedActionProposal ||
    typeof payload.returnedActionProposal !== 'object' ||
    !payload.returnedActionProposal.returnedActionProposalId
  ) {
    throw new Error(failureMessage(response, payload, `Returned Action proposal ${decision} failed`));
  }
};
